import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Translation } from 'react-i18next';
import i18n from 'i18next';
import axios from 'axios';

import { getOnboardingState, getAuthState } from '../../../../store/selectors';
import { getUserData, getCurrentUserId } from '../../../../store/actions';
import countriesList from '../../../../util/countriesList.json';
import Spinner from '../../../../ASUPhotos/spinner.svg';
import AveragePay from './AveragePay';
import JobOpenings from './JobOpenings';
import CareerOutlook from './CareerOutlook';

const BASE_URL = `${process.env.REACT_APP_API}/projected`;

// Reusable component to display the average pay and current job openings for a specific career
class CareerOneStop extends Component {
  // Prevent setState on unmounted components or else we get errors in the console
  _isMounted = false;

  constructor(props) {
    super(props);
    this.state = {
      projectedEmploymentUSA: null,
      projectedEmploymentState: null,
      projectedSalaryUSA: null,
      projectedSalaryState: null,
      careerOutlook: null,
      stateCode: '',
      isLoading: false,
    };
  }

  async componentDidMount() {
    this._isMounted = true;
    await this.getUserData();

    const code = this.props.career.onetCode;

    this.getProjectedSalary(code);
    this.getProjectedEmployment(code);
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  async componentDidUpdate(prevProps, prevState) {
    if (
      this.props.career?.onetCode !== prevProps.career?.onetCode &&
      this._isMounted
    ) {
      /* eslint-disable */
      this.setState(
        {
          isLoading: true,
          projectedEmploymentUSA: null,
          projectedEmploymentState: null,
          projectedSalaryUSA: null,
          projectedSalaryState: null,
        },
        async () => {
          await this.getUserData();
          const code = this.props.career.onetCode;
          this.getProjectedEmployment(code);
          this.getProjectedSalary(code);
        }
      );
    }
    /* eslint-disable */
    if (prevState !== this.state) {
      if (
        this.state.projectedEmploymentUSA !== null &&
        this.state.projectedEmploymentState !== null &&
        this.state.projectedSalaryUSA !== null &&
        this.state.projectedSalaryState !== null &&
        this.state.isLoading !== false &&
        this._isMounted
      ) {
        /* eslint-disable */
        this.setState({
          isLoading: false,
        });
        /* eslint-enable */
      }
    }
  }

  get apiConfig() {
    return {
      headers: {
        Authorization: this.props.auth.jwtToken,
      },
    };
  }

  getUserData = async () => {
    if (typeof this.state.userId !== 'number') {
      await this.getUserId();
    }

    await this.props.getUserData(this.state.userId, this.props.auth.jwtToken);
    this.getKeyByValue(this.props.onboarding.userData.user.state);
  };

  getUserId = async () => {
    const currentUserId = await this.props.getCurrentUserId(
      this.props.auth.jwtToken
    );
    if (currentUserId && this._isMounted) {
      this.setState({
        userId: currentUserId,
      });
    }
  };

  getKeyByValue(value) {
    const stateCode = Object.keys(
      countriesList['United States'].territories
    ).find((key) => countriesList['United States'].territories[key] === value);

    if (this._isMounted) {
      this.setState({
        stateCode,
      });
    }

    return stateCode;
  }

  formatSalary(data) {
    const withoutCommas = data.replace(/,/g, '');
    const unformatted = Number(withoutCommas);
    const hourly = (unformatted / 52 / 40).toFixed(2).toString();
    const annualSalaryUSA = data
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const salary = `${hourly} / hr ($${annualSalaryUSA})`;

    return salary;
  }

  getProjectedSalary(code) {
    const { stateCode } = this.state;
    const apiPath = `${BASE_URL}/salary/${code}/${stateCode}`;

    console.log(
      `%cGetting CareerOneStop Info For '${code} in ${stateCode}'...`,
      'font-weight:bold;color:green'
    );

    axios
      .get(apiPath, this.apiConfig)
      .then((res) => {
        const data = res.data.data.LMI;

        if (data.AveragePayNational && this._isMounted) {
          const salary = this.formatSalary(data.AveragePayNational);
          // DEV NOTE: CareerOneStop doesn't have a list of possible career outlook labels (i.e. "Below Average, Average, Bright") \
          // so this may not cover all actual "Bright Outlook" careers --> Ref. PR for EAHS 2059 for more info
          const outlook = data.CareerOutLook.includes('Bright');

          this.setState({
            projectedSalaryUSA: salary,
            careerOutlook: outlook ? i18n.t('Bright Outlook') : false,
          });
        } else if (this._isMounted) {
          this.setState({
            projectedSalaryUSA: false,
            careerOutlook: false,
          });
        }

        if (data.AveragePayState && this._isMounted) {
          const salary = this.formatSalary(data.AveragePayState);

          this.setState({
            projectedSalaryState: salary,
          });
        } else if (this._isMounted) {
          this.setState({
            projectedSalaryState: false,
          });
        }
      })
      .catch((error) => {
        console.error('Could not get projected salary data', error);
      });
  }

  async getProjectedEmployment(code) {
    const { stateCode } = this.state;
    const apiPath = `${BASE_URL}/employment/${code}/${stateCode}`;

    await axios
      .get(apiPath, this.apiConfig)
      .then((res) => {
        const data = res.data.data.OccupationDetail[0].Projections.Projections;

        data.map((projection) => {
          const employment =
            projection.ProjectedAnnualJobOpening.toString().replace(
              /\B(?=(\d{3})+(?!\d))/g,
              ','
            );

          if (projection.StateName === 'United States') {
            this.setState({
              projectedEmploymentUSA: employment,
            });
          }
          if (projection.StateName !== 'United States') {
            this.setState({
              projectedEmploymentState: employment,
            });
          }
        });
      })
      .catch((error) => {
        console.error('Could not get projected employment data', error);
      });

    this.props.careerInfoLoading();
  }

  titleTextPrimary = {
    color: `${this.props.tenant.primary}`,
  };

  render() {
    const { isLoading } = this.state;

    return isLoading ? (
      <div
        className="pay-and-openings-loading-spinner"
        style={{ justifyContent: 'center' }}
      >
        <img src={Spinner} alt="Loading..." />
      </div>
    ) : (
      <Translation>
        {(t) => (
          <>
            {this.state.projectedSalaryUSA ||
            this.state.projectedEmploymentUSA ? (
              <div className="pay-and-openings">
                {this.state.projectedSalaryUSA ? (
                  <AveragePay
                    modalStyling={this.props.modalStyling}
                    titleTextPrimary={this.titleTextPrimary}
                    titleText={t('Average National Pay:')}
                    plainText={`$${this.state.projectedSalaryUSA}`}
                  />
                ) : null}
                {this.state.projectedEmploymentUSA ? (
                  <JobOpenings
                    modalStyling={this.props.modalStyling}
                    titleTextPrimary={this.titleTextPrimary}
                    titleText={t('Job Openings Nationally:')}
                    plainText={this.state.projectedEmploymentUSA}
                  />
                ) : null}
                {this.state.projectedSalaryState ? (
                  <AveragePay
                    overRideStyles="padding-top"
                    modalStyling={this.props.modalStyling}
                    titleTextPrimary={this.titleTextPrimary}
                    titleText={t('Average Pay In {{location}}:', {
                      location: this.state.stateCode,
                    })}
                    plainText={`$${this.state.projectedSalaryState}`}
                  />
                ) : null}
                {this.state.projectedEmploymentState ? (
                  <JobOpenings
                    modalStyling={this.props.modalStyling}
                    titleTextPrimary={this.titleTextPrimary}
                    titleText={t('Job Openings in {{location}}:', {
                      location: this.state.stateCode,
                    })}
                    plainText={this.state.projectedEmploymentState}
                  />
                ) : null}
                {this.state.careerOutlook ? (
                  <CareerOutlook
                    modalStyling={this.props.modalStyling}
                    plainText={this.state.careerOutlook}
                    setParentOnClick={this.props.setParentOnClick}
                  />
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </Translation>
    );
  }
}

const mapStateToProps = (state) => {
  const onboarding = getOnboardingState(state);
  const auth = getAuthState(state);

  return { onboarding, auth };
};

export default connect(mapStateToProps, { getUserData, getCurrentUserId })(
  CareerOneStop
);
